import React, { useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import * as documentService from "../../../services/documentService";

// ─── Constants ───

const MAX_FILES = 10;
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const ALLOWED_TYPES = {
  "application/pdf": ["pdf"],
  "application/msword": ["doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.ms-excel": ["xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.ms-powerpoint": ["ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ["pptx"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "application/zip": ["zip"],
};

const ALLOWED_EXTENSIONS = Object.values(ALLOWED_TYPES).flat();

// ─── Helpers ───

function getFileExtension(fileName) {
  const parts = fileName?.split(".") || [];
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function getFileNameWithoutExtension(fileName) {
  const parts = fileName?.split(".") || [];
  if (parts.length > 1) {
    parts.pop();
  }
  return parts.join(".");
}

function validateFile(file) {
  const errors = [];
  const ext = getFileExtension(file.name);

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File exceeds 25 MB limit (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
  }

  // Check MIME type
  const allowedExts = ALLOWED_TYPES[file.type];
  if (!allowedExts) {
    errors.push(`Unsupported format "${ext || file.type}". Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
  } else if (!allowedExts.includes(ext)) {
    errors.push(`File extension ".${ext}" does not match its type`);
  }

  return errors;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// File ID counter for unique keys
let fileIdCounter = 0;

// ─── Component ───

function getResultSeverity(result) {
  if (result.failed === 0) return "success";
  if (result.success > 0) return "warning";
  return "error";
}

function getResultMessage(result) {
  if (result.success > 0 && result.failed === 0) {
    const plural = result.success > 1 ? "s" : "";
    return `All ${result.success} file${plural} uploaded successfully.`;
  }
  if (result.success > 0 && result.failed > 0) {
    const total = result.success + result.failed;
    return `${result.success} of ${total} files uploaded successfully. ${result.failed} failed.`;
  }
  return "Upload failed. Please try again.";
}

function getUploadButtonLabel(uploading, validCount) {
  if (uploading) return "Uploading...";
  if (validCount > 1) return `Upload (${validCount})`;
  return "Upload";
}

function getEntryBorderColor(status) {
  if (status === "error") return "error.light";
  if (status === "success") return "success.light";
  return "grey.300";
}

function getEntryBgColor(status) {
  if (status === "error") return "error.50";
  if (status === "success") return "success.50";
  return "background.paper";
}

export default function DocumentUpload({ open, onClose, folderId, departmentId, onUploadComplete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  // Reset state when dialog closes
  const handleClose = useCallback(() => {
    if (uploading) return;
    setFiles([]);
    setUploadResult(null);
    onClose?.();
  }, [uploading, onClose]);

  // ─── File Selection ───

  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    const currentCount = files.length;
    const availableSlots = MAX_FILES - currentCount;

    if (availableSlots <= 0) return;

    const filesToAdd = fileArray.slice(0, availableSlots).map((file) => {
      const validationErrors = validateFile(file);
      fileIdCounter += 1;
      return {
        id: `${file.name}-${file.size}-${fileIdCounter}`,
        file,
        title: getFileNameWithoutExtension(file.name),
        description: "",
        tags: [],
        tagInput: "",
        validationErrors,
        status: validationErrors.length > 0 ? "error" : "pending",
      };
    });

    setFiles((prev) => [...prev, ...filesToAdd]);
    setUploadResult(null);
  }, [files.length]);

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) {
      addFiles(e.target.files);
    }
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // ─── Drag & Drop ───

  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  };

  // ─── Per-file Metadata ───

  const updateFileField = (fileId, field, value) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, [field]: value } : f))
    );
  };

  const handleTagKeyDown = (fileId, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const file = files.find((f) => f.id === fileId);
      const value = file?.tagInput?.trim();
      if (!value) return;
      if (file.tags.length >= 5) return;
      if (value.length > 50) return;
      if (file.tags.includes(value)) return;

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, tags: [...f.tags, value], tagInput: "" }
            : f
        )
      );
    }
  };

  const removeTag = (fileId, tagToRemove) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? { ...f, tags: f.tags.filter((t) => t !== tagToRemove) }
          : f
      )
    );
  };

  // ─── Upload ───

  const handleUpload = async () => {
    const validFiles = files.filter((f) => f.status !== "error");
    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadResult(null);

    // Mark valid files as uploading
    setFiles((prev) =>
      prev.map((f) =>
        f.status === "pending" ? { ...f, status: "uploading" } : f
      )
    );

    try {
      if (validFiles.length === 1) {
        // Single upload
        const entry = validFiles[0];
        const formData = new FormData();
        formData.append("file", entry.file);
        formData.append("title", entry.title || getFileNameWithoutExtension(entry.file.name));
        if (entry.description) formData.append("description", entry.description);
        if (entry.tags.length > 0) formData.append("tags", JSON.stringify(entry.tags));
        formData.append("folderId", folderId);
        if (departmentId) formData.append("departmentId", departmentId);

        await documentService.uploadDocument(formData);

        setFiles((prev) =>
          prev.map((f) =>
            f.id === entry.id ? { ...f, status: "success" } : f
          )
        );
        setUploadResult({ success: 1, failed: 0, errors: [] });
      } else {
        // Bulk upload
        const formData = new FormData();
        const titles = [];

        validFiles.forEach((entry) => {
          formData.append("files", entry.file);
          titles.push(entry.title || getFileNameWithoutExtension(entry.file.name));
        });

        formData.append("titles", JSON.stringify(titles));
        formData.append("folderId", folderId);
        if (departmentId) formData.append("departmentId", departmentId);

        const response = await documentService.bulkUpload(formData);
        const data = response?.data?.data || response?.data || {};

        const successCount = data.successful?.length || data.successCount || 0;
        const rejectedList = data.rejected || data.errors || [];
        const failedCount = rejectedList.length || data.failedCount || 0;

        // Update individual file statuses
        setFiles((prev) =>
          prev.map((f) => {
            if (f.status !== "uploading") return f;
            const rejected = rejectedList.find(
              (r) => r.fileName === f.file.name || r.index === validFiles.indexOf(f)
            );
            if (rejected) {
              return { ...f, status: "error", validationErrors: [rejected.reason || rejected.error || "Upload failed"] };
            }
            return { ...f, status: "success" };
          })
        );

        setUploadResult({
          success: successCount,
          failed: failedCount,
          errors: rejectedList,
        });
      }

      onUploadComplete?.();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Upload failed";

      // Mark uploading files as errored
      setFiles((prev) =>
        prev.map((f) =>
          f.status === "uploading"
            ? { ...f, status: "error", validationErrors: [message] }
            : f
        )
      );

      setUploadResult({ success: 0, failed: validFiles.length, errors: [{ reason: message }] });
    } finally {
      setUploading(false);
    }
  };

  // ─── Computed Values ───

  const validCount = files.filter((f) => f.status === "pending" || f.status === "uploading").length;
  const hasValidFiles = files.some((f) => f.status === "pending");
  const allDone = uploadResult !== null;

  // ─── Render ───

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 400 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Upload Documents
        <IconButton onClick={handleClose} disabled={uploading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Upload Result Banner */}
        {uploadResult && (
          <Alert
            severity={getResultSeverity(uploadResult)}
            sx={{ mb: 2 }}
          >
            {getResultMessage(uploadResult)}
          </Alert>
        )}

        {/* Drag-and-drop Zone */}
        {files.length < MAX_FILES && !uploading && (
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: dragOver ? "primary.main" : "grey.400",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: dragOver ? "action.hover" : "grey.50",
              transition: "all 0.2s",
              mb: files.length > 0 ? 2 : 0,
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "grey.500", mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Drag & drop files here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max {MAX_FILES} files, 25 MB each. Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, ZIP
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileInputChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.zip"
            />
          </Box>
        )}

        {/* Progress bar */}
        {uploading && <LinearProgress sx={{ mb: 2 }} />}

        {/* File List */}
        {files.length > 0 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {files.map((entry, index) => (
              <Box
                key={entry.id}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: getEntryBorderColor(entry.status),
                  borderRadius: 1,
                  backgroundColor: getEntryBgColor(entry.status),
                }}
              >
                {/* File Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {entry.status === "success" && <CheckCircleIcon color="success" fontSize="small" />}
                  {entry.status === "error" && <ErrorIcon color="error" fontSize="small" />}
                  {(entry.status === "pending" || entry.status === "uploading") && (
                    <InsertDriveFileIcon color="action" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }} noWrap>
                    {entry.file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {formatFileSize(entry.file.size)}
                  </Typography>
                  {!uploading && entry.status !== "success" && (
                    <IconButton size="small" onClick={() => removeFile(entry.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Validation Errors */}
                {entry.validationErrors.length > 0 && (
                  <Alert severity="error" variant="outlined" sx={{ mb: 1, py: 0 }}>
                    {entry.validationErrors.map((err) => (
                      <Typography key={err} variant="caption" display="block">
                        {err}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {/* Metadata Fields (only for valid pending files) */}
                {entry.status === "pending" && entry.validationErrors.length === 0 && (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      label="Title"
                      size="small"
                      fullWidth
                      value={entry.title}
                      onChange={(e) => updateFileField(entry.id, "title", e.target.value)}
                      placeholder={getFileNameWithoutExtension(entry.file.name)}
                      inputProps={{ maxLength: 200 }}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      label="Description (optional)"
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={3}
                      value={entry.description}
                      onChange={(e) => updateFileField(entry.id, "description", e.target.value)}
                      inputProps={{ maxLength: 1000 }}
                      sx={{ mb: 1 }}
                    />
                    {/* Tags */}
                    <Box>
                      <TextField
                        label="Tags (press Enter to add)"
                        size="small"
                        fullWidth
                        value={entry.tagInput}
                        onChange={(e) => updateFileField(entry.id, "tagInput", e.target.value)}
                        onKeyDown={(e) => handleTagKeyDown(entry.id, e)}
                        disabled={entry.tags.length >= 5}
                        helperText={`${entry.tags.length}/5 tags`}
                        inputProps={{ maxLength: 50 }}
                      />
                      {entry.tags.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {entry.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              onDelete={() => removeTag(entry.id, tag)}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Uploading indicator */}
                {entry.status === "uploading" && (
                  <LinearProgress variant="indeterminate" sx={{ mt: 1, borderRadius: 1 }} />
                )}
              </Box>
            ))}
          </Stack>
        )}

        {/* File Count Info */}
        {files.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {files.length} of {MAX_FILES} files selected
            {validCount > 0 && ` · ${validCount} ready to upload`}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>
          {allDone ? "Close" : "Cancel"}
        </Button>
        {!allDone && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!hasValidFiles || uploading}
            startIcon={<CloudUploadIcon />}
          >
            {getUploadButtonLabel(uploading, validCount)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
